// ===== WebRTC-Based Chat Interface with Direct WebRTC Implementation =====

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { 
  Phone, 
  VideoCall, 
  Send, 
  AttachFile, 
  EmojiEmotions,
  MoreVert,
  ArrowBack,
  CallEnd,
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  VolumeUp,
  VolumeOff,
  ScreenShare,
  StopScreenShare,
  History,
  CallMade,
  CallReceived,
  CallMissed
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import io from 'socket.io-client';
import { socket as globalSocket } from '../../Socket';

const ChatAndCall = ({ currentUser, receiver, onClose, socket }) => {
  const [messages, setMessages] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const processedMessageIds = useRef(new Set());
  const [newMessage, setNewMessage] = useState('');
  const [showCallHistory, setShowCallHistory] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [isInCall, setIsInCall] = useState(false);
  const [callType, setCallType] = useState(null);
  const [callStatus, setCallStatus] = useState('');
  const [callError, setCallError] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenStream, setScreenStream] = useState(null);
  
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  
  // WebRTC Core Components
  const webRTCConnection = useRef(null);
  const localMediaStream = useRef(null);
  const remoteMediaStream = useRef(null);
  const dataChannel = useRef(null);
  const screenShareStream = useRef(null);
  const callStartTime = useRef(null);
  
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = parseInt(process.env.REACT_APP_MAX_RECONNECT_ATTEMPTS) || 5;
  const iceCandidatesQueue = useRef([]);
  
  const chatRoomId = useRef(receiver?.appointmentId || (currentUser?.socketId && receiver?.socketId ? `${currentUser.socketId}-${receiver.socketId}` : 'default-room'));

  const emojis = ['😀', '😂', '😍', '😢', '😮', '😡', '👍', '👏', '❤️', '🙏', '✨', '🎉', '💯', '🔥', '👌', '💪'];

  // WebRTC Configuration with optimized settings
  const webRTCConfig = {
    iceServers: [
      { urls: process.env.REACT_APP_STUN_SERVER_1 || 'stun:stun.l.google.com:19302' },
      { urls: process.env.REACT_APP_STUN_SERVER_2 || 'stun:stun1.l.google.com:19302' },
      { urls: process.env.REACT_APP_STUN_SERVER_3 || 'stun:stun2.l.google.com:19302' },
      { urls: process.env.REACT_APP_STUN_SERVER_4 || 'stun:stun.services.mozilla.com' },
      { urls: process.env.REACT_APP_STUN_SERVER_5 || 'stun:stun.ekiga.net' }
    ],
    iceCandidatePoolSize: parseInt(process.env.REACT_APP_ICE_CANDIDATE_POOL_SIZE) || 10,
    iceTransportPolicy: process.env.REACT_APP_ICE_TRANSPORT_POLICY || 'all',
    bundlePolicy: process.env.REACT_APP_BUNDLE_POLICY || 'max-bundle',
    rtcpMuxPolicy: process.env.REACT_APP_RTCP_MUX_POLICY || 'require'
  };

  // WebRTC Media Constraints
  const getMediaConstraints = useCallback((isVideo) => ({
    video: isVideo ? {
      width: { 
        min: parseInt(process.env.REACT_APP_VIDEO_MIN_WIDTH) || 320, 
        ideal: parseInt(process.env.REACT_APP_VIDEO_IDEAL_WIDTH) || 640, 
        max: parseInt(process.env.REACT_APP_VIDEO_MAX_WIDTH) || 1280 
      },
      height: { 
        min: parseInt(process.env.REACT_APP_VIDEO_MIN_HEIGHT) || 240, 
        ideal: parseInt(process.env.REACT_APP_VIDEO_IDEAL_HEIGHT) || 480, 
        max: parseInt(process.env.REACT_APP_VIDEO_MAX_HEIGHT) || 720 
      },
      frameRate: { 
        ideal: parseInt(process.env.REACT_APP_VIDEO_FRAME_RATE) || 30, 
        max: parseInt(process.env.REACT_APP_VIDEO_FRAME_RATE) || 30 
      },
      facingMode: process.env.REACT_APP_VIDEO_FACING_MODE || 'user'
    } : false,
    audio: {
      echoCancellation: process.env.REACT_APP_AUDIO_ECHO_CANCELLATION === 'true' || true,
      noiseSuppression: process.env.REACT_APP_AUDIO_NOISE_SUPPRESSION === 'true' || true,
      autoGainControl: process.env.REACT_APP_AUDIO_AUTO_GAIN_CONTROL === 'true' || true,
      sampleRate: { ideal: parseInt(process.env.REACT_APP_AUDIO_SAMPLE_RATE) || 44100 }
    }
  }), []);

  // Enhanced error logging with WebRTC context
  const logWebRTCError = useCallback((context, error) => {
    console.error(`[WebRTC ${context}]`, error);
    if (error.name === 'NotAllowedError') {
      setCallError('Camera/microphone access denied. Please allow permissions and try again.');
    } else if (error.name === 'NotFoundError') {
      setCallError('Camera/microphone not found. Please check your devices.');
    } else if (error.name === 'NotReadableError') {
      setCallError('Camera/microphone in use by another application.');
    } else if (error.name === 'OverconstrainedError') {
      setCallError('Camera/microphone constraints not supported by your device.');
    } else {
      setCallError(`WebRTC ${context}: ${error.message}`);
    }
  }, []);

  // WebRTC Connection Management
  const createWebRTCConnection = useCallback(() => {
    console.log('🔧 Creating WebRTC connection...');
    
    try {
      const connection = new RTCPeerConnection(webRTCConfig);

      // WebRTC ICE Candidate Handling
      connection.onicecandidate = (event) => {
        if (event.candidate && socketRef.current) {
          console.log('📡 WebRTC ICE candidate:', event.candidate.type);
          const receiverUserId = receiver?._id || 
                                receiver?.userId || 
                                receiver?.id || 
                                receiver?.doctorId || 
                                receiver?.patientId || 
                                receiver?.user_id || 
                                receiver?.user?._id || 
                                receiver?.user?.id || 
                                receiver?.doctor?._id || 
                                receiver?.patient?._id || 
                                receiver?.receiverId ||
                                receiver?.socketId;
          socketRef.current.emit('webrtc-ice-candidate', {
            candidate: event.candidate,
            receiverId: receiverUserId,
            sessionId: currentSessionId
          });
        } else if (!event.candidate) {
          console.log('🏁 WebRTC ICE gathering complete');
        }
      };

      // WebRTC Track Handling for Remote Media
      connection.ontrack = (event) => {
        console.log('🎬 WebRTC remote track received:', event.track.kind);
        
        if (event.streams && event.streams.length > 0) {
          const remoteStream = event.streams[0];
          remoteMediaStream.current = remoteStream;
          console.log('📺 WebRTC remote stream:', remoteStream.id);
          
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remoteStream;
            remoteVideoRef.current.autoplay = true;
            remoteVideoRef.current.playsInline = true;
            remoteVideoRef.current.muted = false;
            
            const playPromise = remoteVideoRef.current.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  console.log('✅ WebRTC remote video playing');
                  setCallStatus('connected');
                  setCallError('');
                })
                .catch(error => {
                  console.log('❌ WebRTC remote video play failed:', error);
                  const enableVideo = () => {
                    if (remoteVideoRef.current) {
                      remoteVideoRef.current.play()
                        .then(() => {
                          console.log('✅ WebRTC video enabled after interaction');
                          setCallStatus('connected');
                          document.removeEventListener('click', enableVideo);
                        })
                        .catch(e => console.log('❌ WebRTC still failed after interaction:', e));
                    }
                  };
                  document.addEventListener('click', enableVideo, { once: true });
                });
            }
          }
        }
      };

      // WebRTC Connection State Monitoring
      connection.onconnectionstatechange = () => {
        const state = connection.connectionState;
        console.log('🔗 WebRTC connection state:', state);
        
        switch (state) {
          case 'new':
            console.log('🆕 WebRTC connection is new');
            break;
          case 'connecting':
            console.log('🔄 WebRTC connecting...');
            setCallStatus('connecting');
            break;
          case 'connected':
            console.log('🎉 WebRTC connection established!');
            setCallStatus('connected');
            setCallError('');
            break;
          case 'disconnected':
            console.log('🔌 WebRTC connection disconnected - attempting to reconnect...');
            setCallStatus('reconnecting');
            // Don't end call immediately on disconnect, give it time to reconnect
            break;
          case 'failed':
            console.log('💥 WebRTC connection failed - will retry');
            setCallError('Connection issue - retrying...');
            // Don't end call immediately, let ICE restart handle it
            break;
          case 'closed':
            console.log('🚪 WebRTC connection closed');
            // Don't automatically end call on connection closed
            // Let user manually end the call or wait for explicit call-ended signal
            setCallStatus('disconnected');
            setCallError('Connection closed - please try calling again');
            break;
        }
      };

      // WebRTC ICE Connection State
      connection.oniceconnectionstatechange = () => {
        const state = connection.iceConnectionState;
        console.log('🧊 WebRTC ICE connection state:', state);
        
        switch (state) {
          case 'new':
            console.log('🆕 ICE gathering starting');
            break;
          case 'gathering':
            console.log('📡 ICE gathering in progress');
            break;
          case 'checking':
            console.log('🔍 ICE connectivity checks in progress');
            setCallStatus('connecting');
            break;
          case 'connected':
            console.log('✅ ICE connected!');
            setCallStatus('connected');
            setCallError('');
            break;
          case 'completed':
            console.log('✅ ICE completed!');
            setCallStatus('connected');
            setCallError('');
            break;
          case 'disconnected':
            console.log('🔌 ICE disconnected - attempting to reconnect');
            setCallStatus('reconnecting');
            break;
          case 'failed':
            console.log('🆘 ICE connection failed - attempting restart');
            try {
              connection.restartIce();
              setCallError('Connection issue - retrying...');
              
              // Give much more time for ICE restart to work
              setTimeout(() => {
                if (connection && connection.iceConnectionState === 'failed') {
                  console.log('ICE restart failed after 30 seconds, but keeping call active');
                  setCallError('Poor connection - call may have reduced quality');
                  // Don't end the call, let user decide
                }
              }, 30000); // 30 seconds timeout, much more generous
            } catch (error) {
              console.error('ICE restart failed:', error);
              setCallError('Connection issue - please check your network');
              // Don't automatically end call on ICE restart failure
            }
            break;
          case 'closed':
            console.log('🚪 ICE connection closed');
            break;
        }
      };

      // WebRTC ICE Gathering State
      connection.onicegatheringstatechange = () => {
        console.log('📊 WebRTC ICE gathering state:', connection.iceGatheringState);
      };

      // WebRTC Signaling State
      connection.onsignalingstatechange = () => {
        console.log('📡 WebRTC signaling state:', connection.signalingState);
      };

      // WebRTC Data Channel for messaging
      const channel = connection.createDataChannel('messages', {
        ordered: true
      });

      channel.onopen = () => {
        console.log('📬 WebRTC data channel opened');
      };

      channel.onmessage = (event) => {
        console.log('📨 WebRTC data channel message:', event.data);
        try {
          const messageData = JSON.parse(event.data);
          if (messageData.type === 'chat') {
            handleDataChannelMessage(messageData);
          }
        } catch (error) {
          console.error('Error parsing data channel message:', error);
        }
      };

      channel.onclose = () => {
        console.log('📪 WebRTC data channel closed');
      };

      dataChannel.current = channel;

      // Handle incoming data channel
      connection.ondatachannel = (event) => {
        const incomingChannel = event.channel;
        console.log('📬 WebRTC incoming data channel:', incomingChannel.label);
        
        incomingChannel.onmessage = (event) => {
          console.log('📨 WebRTC incoming data channel message:', event.data);
          try {
            const messageData = JSON.parse(event.data);
            if (messageData.type === 'chat') {
              handleDataChannelMessage(messageData);
            }
          } catch (error) {
            console.error('Error parsing incoming data channel message:', error);
          }
        };
      };

      return connection;
    } catch (error) {
      logWebRTCError('Connection Creation', error);
      return null;
    }
  }, [receiver?.socketId, currentSessionId, logWebRTCError]);

  // WebRTC Media Stream Management
  const getWebRTCMediaStream = useCallback(async (isVideo) => {
    try {
      console.log('📱 WebRTC requesting media stream...');
      const constraints = getMediaConstraints(isVideo);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      localMediaStream.current = stream;
      console.log('✅ WebRTC media stream obtained:', stream.getTracks().map(t => `${t.kind}: ${t.enabled}`));
      
      if (localVideoRef.current && isVideo) {
        localVideoRef.current.srcObject = stream;
        localVideoRef.current.muted = true;
        await localVideoRef.current.play();
        console.log('✅ WebRTC local video playing');
      }
      
      return stream;
    } catch (error) {
      logWebRTCError('Media Stream', error);
      throw error;
    }
  }, [getMediaConstraints, logWebRTCError]);

  // WebRTC Call Controls
  const toggleWebRTCMute = useCallback(() => {
    try {
      if (localMediaStream.current) {
        const audioTrack = localMediaStream.current.getAudioTracks()[0];
        if (audioTrack) {
          audioTrack.enabled = !audioTrack.enabled;
          setIsMuted(!audioTrack.enabled);
          console.log(`WebRTC Audio ${audioTrack.enabled ? 'unmuted' : 'muted'}`);
          
          // Send mute status via data channel
          if (dataChannel.current && dataChannel.current.readyState === 'open') {
            dataChannel.current.send(JSON.stringify({
              type: 'control',
              action: 'mute',
              muted: !audioTrack.enabled
            }));
          }
        }
      }
    } catch (error) {
      logWebRTCError('Toggle Mute', error);
    }
  }, [logWebRTCError]);

  const toggleWebRTCVideo = useCallback(() => {
    try {
      if (localMediaStream.current && callType === 'video') {
        const videoTrack = localMediaStream.current.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.enabled = !videoTrack.enabled;
          setIsVideoOff(!videoTrack.enabled);
          console.log(`WebRTC Video ${videoTrack.enabled ? 'enabled' : 'disabled'}`);
          
          // Send video status via data channel
          if (dataChannel.current && dataChannel.current.readyState === 'open') {
            dataChannel.current.send(JSON.stringify({
              type: 'control',
              action: 'video',
              videoOff: !videoTrack.enabled
            }));
          }
        }
      }
    } catch (error) {
      logWebRTCError('Toggle Video', error);
    }
  }, [callType, logWebRTCError]);

  const toggleWebRTCSpeaker = useCallback(() => {
    setIsSpeakerOn(!isSpeakerOn);
    console.log(`WebRTC Speaker ${!isSpeakerOn ? 'on' : 'off'}`);
    
    // Send speaker status via data channel
    if (dataChannel.current && dataChannel.current.readyState === 'open') {
      dataChannel.current.send(JSON.stringify({
        type: 'control',
        action: 'speaker',
        speakerOn: !isSpeakerOn
      }));
    }
  }, [isSpeakerOn]);

  // WebRTC Screen Sharing Functions
  const startScreenShare = useCallback(async () => {
    try {
      console.log('🖥️ Starting screen share...');
      
      // Get screen share stream
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          displaySurface: 'monitor'
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      screenShareStream.current = screenStream;
      setScreenStream(screenStream);
      setIsScreenSharing(true);
      
      console.log('✅ Screen share stream obtained');

      // Replace video track in WebRTC connection
      if (webRTCConnection.current && localMediaStream.current) {
        const videoTrack = screenStream.getVideoTracks()[0];
        const sender = webRTCConnection.current.getSenders().find(s => 
          s.track && s.track.kind === 'video'
        );
        
        if (sender && videoTrack) {
          await sender.replaceTrack(videoTrack);
          console.log('✅ Screen share track replaced in WebRTC connection');
        }

        // Update local video display
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = screenStream;
        }
      }

      // Send screen share status via data channel
      if (dataChannel.current && dataChannel.current.readyState === 'open') {
        dataChannel.current.send(JSON.stringify({
          type: 'control',
          action: 'screen-share',
          isScreenSharing: true
        }));
      }

      // Handle screen share ended event (when user stops sharing via browser UI)
      const videoTrack = screenStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = () => {
          console.log('🖥️ Screen share ended by user');
          stopScreenShare();
        };
      }

    } catch (error) {
      console.error('❌ Screen share failed:', error);
      logWebRTCError('Screen Share', error);
      setIsScreenSharing(false);
    }
  }, [logWebRTCError]);

  const stopScreenShare = useCallback(async () => {
    try {
      console.log('🖥️ Stopping screen share...');
      
      // Stop screen share stream
      if (screenShareStream.current) {
        screenShareStream.current.getTracks().forEach(track => {
          track.stop();
        });
        screenShareStream.current = null;
      }
      
      setScreenStream(null);
      setIsScreenSharing(false);

      // Get camera stream back
      if (webRTCConnection.current && localMediaStream.current && callType === 'video') {
        try {
          const cameraStream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { min: 320, ideal: 640, max: 1280 },
              height: { min: 240, ideal: 480, max: 720 },
              frameRate: { ideal: 30, max: 30 },
              facingMode: 'user'
            },
            audio: false // Don't get audio again, keep existing audio track
          });

          const videoTrack = cameraStream.getVideoTracks()[0];
          const sender = webRTCConnection.current.getSenders().find(s => 
            s.track && s.track.kind === 'video'
          );
          
          if (sender && videoTrack) {
            await sender.replaceTrack(videoTrack);
            console.log('✅ Camera track restored in WebRTC connection');
            
            // Update local media stream with new video track
            const audioTracks = localMediaStream.current.getAudioTracks();
            localMediaStream.current = new MediaStream([videoTrack, ...audioTracks]);
            
            // Update local video display
            if (localVideoRef.current) {
              localVideoRef.current.srcObject = localMediaStream.current;
            }
          }
        } catch (error) {
          console.error('❌ Failed to restore camera:', error);
          logWebRTCError('Camera Restore', error);
        }
      }

      // Send screen share status via data channel
      if (dataChannel.current && dataChannel.current.readyState === 'open') {
        dataChannel.current.send(JSON.stringify({
          type: 'control',
          action: 'screen-share',
          isScreenSharing: false
        }));
      }

    } catch (error) {
      console.error('❌ Stop screen share failed:', error);
      logWebRTCError('Stop Screen Share', error);
    }
  }, [callType, logWebRTCError]);

  const toggleScreenShare = useCallback(() => {
    if (isScreenSharing) {
      stopScreenShare();
    } else {
      startScreenShare();
    }
  }, [isScreenSharing, startScreenShare, stopScreenShare]);

  // Data Channel Message Handler
  const handleDataChannelMessage = useCallback((messageData) => {
    if (messageData.type === 'chat') {
      const message = {
        id: messageData.id,
        text: messageData.text,
        sender: messageData.sender,
        receiver: messageData.receiver,
        timestamp: new Date(messageData.timestamp),
        type: 'text'
      };
      
      if (!processedMessageIds.current.has(message.id)) {
        processedMessageIds.current.add(message.id);
        setMessages(prev => [...prev, message]);
      }
    } else if (messageData.type === 'control') {
      // Handle control messages (mute, video, speaker status)
      console.log('WebRTC control message:', messageData);
    }
  }, []);

  // WebRTC Call Initiation
  const startWebRTCCall = useCallback(async (isVideo = false) => {
    try {
      console.log(`🚀 Starting WebRTC ${isVideo ? 'video' : 'audio'} call...`);
      console.log('🔍 Setting isInCall to true immediately');
      
      setCallType(isVideo ? 'video' : 'audio');
      setIsInCall(true);
      setCallStatus('calling');
      setCallError('');
      setIncomingCall(null);
      
      console.log('✅ Call state set - isInCall should be true now');

      // Generate session ID first
      const sessionId = `webrtc-${currentUser.socketId}-${receiver.socketId}-${Date.now()}`;
      setCurrentSessionId(sessionId);
      console.log('📋 Call session ID:', sessionId);

      // Get media stream
      console.log('📱 Getting media stream...');
      const stream = await getWebRTCMediaStream(isVideo);
      console.log('✅ Media stream obtained');
      
      // Create WebRTC connection
      console.log('🔧 Creating WebRTC connection...');
      webRTCConnection.current = createWebRTCConnection();
      if (!webRTCConnection.current) {
        throw new Error('Failed to create WebRTC connection');
      }
      console.log('✅ WebRTC connection created');

      // Add tracks to WebRTC connection
      console.log('➕ Adding tracks to connection...');
      stream.getTracks().forEach((track) => {
        console.log(`➕ Adding WebRTC ${track.kind} track:`, track.enabled);
        webRTCConnection.current.addTrack(track, stream);
      });
      console.log('✅ All tracks added');

      // Create WebRTC offer
      console.log('📝 Creating offer...');
      const offer = await webRTCConnection.current.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: isVideo
      });
      console.log('✅ Offer created');
      
      await webRTCConnection.current.setLocalDescription(offer);
      console.log('✅ Local description set');

      console.log('📤 Sending WebRTC offer to:', receiver.socketId);
      
      // Wait for socket connection if not already connected
      if (!socketRef.current || !socketRef.current.connected) {
        console.log('⏳ Waiting for socket connection...');
        
        // Wait up to 5 seconds for socket connection
        const maxWaitTime = 5000;
        const startTime = Date.now();
        
        while ((!socketRef.current || !socketRef.current.connected) && (Date.now() - startTime < maxWaitTime)) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      if (socketRef.current && socketRef.current.connected) {
        // Use the same comprehensive receiver ID resolution as messaging
        const receiverUserId = receiver._id || 
                              receiver.userId || 
                              receiver.id || 
                              receiver.doctorId || 
                              receiver.patientId || 
                              receiver.user_id || 
                              receiver.user?._id || 
                              receiver.user?.id || 
                              receiver.doctor?._id || 
                              receiver.patient?._id || 
                              receiver.receiverId ||
                              receiver.socketId;
        
        socketRef.current.emit('webrtc-call-offer', {
          offer,
          receiverId: receiverUserId,
          callerId: currentUser.socketId,
          callerName: currentUser.name || 'Unknown',
          callType: isVideo ? 'video' : 'audio',
          appointmentId: receiver.appointmentId,
          sessionId
        });
        console.log('✅ WebRTC offer sent successfully to user:', receiverUserId);
        setCallStatus('ringing');
      } else {
        throw new Error('Socket not connected after waiting');
      }

    } catch (error) {
      console.error('❌ Call start failed:', error);
      logWebRTCError('Start Call', error);
      // Only end call if it's a critical error, give more time for recovery
      if (error.name === 'NotAllowedError' || error.name === 'NotFoundError') {
        setTimeout(() => endWebRTCCall(), 5000); // Give 5 seconds for user to fix permissions
      }
      // For other errors, keep the call active and let user try again
    }
  }, [currentUser.socketId, receiver.socketId, receiver.appointmentId, getWebRTCMediaStream, createWebRTCConnection, logWebRTCError]);

  // WebRTC Call Handling
  const handleWebRTCOffer = useCallback(async (data) => {
    try {
      console.log('📞 WebRTC incoming call from:', data.callerId, 'Type:', data.callType);
      console.log('📞 Full offer data:', data);
      console.log('📞 Setting incoming call state...');
      
      setIncomingCall({
        callerId: data.callerId,
        callType: data.callType,
        offer: data.offer,
        sessionId: data.sessionId,
        callerName: receiver.name || 'Unknown'
      });
      
      console.log('✅ Incoming call state set successfully');

    } catch (error) {
      console.error('❌ Error handling WebRTC offer:', error);
      logWebRTCError('Handle Offer', error);
    }
  }, [receiver.name, logWebRTCError]);

  const acceptWebRTCCall = useCallback(async () => {
    if (!incomingCall) return;
    
    try {
      console.log('✅ Accepting WebRTC call');
      
      setCallType(incomingCall.callType);
      setIsInCall(true);
      setCallStatus('connecting');
      setCurrentSessionId(incomingCall.sessionId);
      setIncomingCall(null);

      // Get media stream
      const stream = await getWebRTCMediaStream(incomingCall.callType === 'video');
      
      // Create WebRTC connection
      webRTCConnection.current = createWebRTCConnection();
      if (!webRTCConnection.current) {
        throw new Error('Failed to create WebRTC connection');
      }

      // Add tracks to WebRTC connection
      stream.getTracks().forEach(track => {
        console.log(`➕ Adding WebRTC ${track.kind} track for answer`);
        webRTCConnection.current.addTrack(track, stream);
      });

      // Set remote description
      await webRTCConnection.current.setRemoteDescription(incomingCall.offer);

      // Process queued ICE candidates
      while (iceCandidatesQueue.current.length > 0) {
        const candidate = iceCandidatesQueue.current.shift();
        await webRTCConnection.current.addIceCandidate(candidate);
      }

      // Create WebRTC answer
      const answer = await webRTCConnection.current.createAnswer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: incomingCall.callType === 'video'
      });
      
      await webRTCConnection.current.setLocalDescription(answer);

      console.log('📤 Sending WebRTC answer...');
      socketRef.current.emit('webrtc-call-answer', {
        answer,
        callerId: incomingCall.callerId,
        sessionId: incomingCall.sessionId
      });

    } catch (error) {
      logWebRTCError('Accept Call', error);
      setIncomingCall(null);
      setTimeout(() => endWebRTCCall(), 3000);
    }
  }, [incomingCall, getWebRTCMediaStream, createWebRTCConnection, logWebRTCError]);

  const rejectWebRTCCall = useCallback(() => {
    if (!incomingCall) return;
    
    console.log('❌ Rejecting WebRTC call');
    socketRef.current.emit('webrtc-call-rejected', {
      receiverId: incomingCall.callerId,
      sessionId: incomingCall?.sessionId
    });
    setIncomingCall(null);
  }, [incomingCall]);

  const handleWebRTCAnswer = useCallback(async (data) => {
    try {
      console.log('📞 Received WebRTC answer');
      console.log('🔍 Current isInCall:', isInCall);
      console.log('🔍 Current callStatus:', callStatus);
      
      if (webRTCConnection.current) {
        await webRTCConnection.current.setRemoteDescription(data.answer);
        console.log('✅ Remote description set from answer');
        setCallStatus('connecting');
        
        // Ensure we're marked as in call
        if (!isInCall) {
          console.log('⚠️ Setting isInCall to true from answer');
          setIsInCall(true);
        }
        
        // Process queued ICE candidates
        while (iceCandidatesQueue.current.length > 0) {
          const candidate = iceCandidatesQueue.current.shift();
          await webRTCConnection.current.addIceCandidate(candidate);
          console.log('✅ Processed queued ICE candidate');
        }
        
        setTimeout(() => {
          setCallStatus(prevStatus => {
            console.log('🔄 Updating call status from', prevStatus, 'to connected');
            if (prevStatus === 'connecting') {
              return 'connected';
            }
            return prevStatus;
          });
        }, 3000);
      }
    } catch (error) {
      logWebRTCError('Handle Answer', error);
    }
  }, [logWebRTCError, isInCall, callStatus]);

  const handleWebRTCIceCandidate = useCallback(async (data) => {
    try {
      if (data.candidate) {
        const candidate = new RTCIceCandidate(data.candidate);
        
        if (webRTCConnection.current && webRTCConnection.current.remoteDescription) {
          await webRTCConnection.current.addIceCandidate(candidate);
          console.log('✅ WebRTC ICE candidate added');
        } else {
          // Queue the candidate if remote description is not set yet
          iceCandidatesQueue.current.push(candidate);
          console.log('📋 WebRTC ICE candidate queued');
        }
      }
    } catch (error) {
      console.error('Error adding WebRTC ICE candidate:', error);
    }
  }, []);

  // WebRTC Call Termination
  const endWebRTCCall = useCallback(() => {
    console.log('🔚 Ending WebRTC call...');
    
    try {
      // Calculate call duration
      const callEndTime = new Date();
      const callDuration = callStartTime.current ? 
        Math.floor((callEndTime - callStartTime.current) / 1000) : 0;

      // Stop screen sharing if active
      if (isScreenSharing && screenShareStream.current) {
        console.log('🖥️ Stopping screen share due to call end...');
        screenShareStream.current.getTracks().forEach(track => {
          track.stop();
        });
        screenShareStream.current = null;
        setScreenStream(null);
        setIsScreenSharing(false);
      }

      // Stop all media tracks
      if (localMediaStream.current) {
        localMediaStream.current.getTracks().forEach(track => {
          console.log(`⏹️ Stopping WebRTC ${track.kind} track`);
          track.stop();
        });
        localMediaStream.current = null;
      }

      // Close data channel
      if (dataChannel.current) {
        dataChannel.current.close();
        dataChannel.current = null;
      }

      // Close WebRTC connection
      if (webRTCConnection.current) {
        webRTCConnection.current.close();
        webRTCConnection.current = null;
      }

      // Clear video elements
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null;
      }
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
      }

      // Clear remote stream reference
      remoteMediaStream.current = null;

      // Clear ICE candidates queue
      iceCandidatesQueue.current = [];

      // Add call to history
      const callRecord = {
        id: Date.now() + Math.random(),
        type: callType || 'audio',
        direction: 'outgoing',
        participant: receiver.name || 'Unknown',
        participantId: receiver.socketId || receiver._id,
        duration: callDuration,
        timestamp: callEndTime,
        status: callStatus === 'connected' ? 'completed' : 'missed'
      };
      
      setCallHistory(prev => [callRecord, ...prev]);
      console.log('📞 Call added to history:', callRecord);

      // Notify the other user with correct user ID
      if (socketRef.current && socketRef.current.connected) {
        const receiverUserId = receiver._id || 
                              receiver.userId || 
                              receiver.id || 
                              receiver.doctorId || 
                              receiver.patientId || 
                              receiver.user_id || 
                              receiver.user?._id || 
                              receiver.user?.id || 
                              receiver.doctor?._id || 
                              receiver.patient?._id || 
                              receiver.receiverId ||
                              receiver.socketId;
        
        socketRef.current.emit('webrtc-call-ended', {
          receiverId: receiverUserId,
          sessionId: currentSessionId
        });
        console.log('🔚 WebRTC call ended notification sent to user:', receiverUserId);
      }

      // Reset call state (including screen sharing state)
      setIsInCall(false);
      setCallType(null);
      setCallStatus('');
      setCallError('');
      setIsMuted(false);
      setIsVideoOff(false);
      setIsScreenSharing(false);
      setScreenStream(null);
      setCurrentSessionId(null);
      setIncomingCall(null);

    } catch (error) {
      logWebRTCError('End Call', error);
    }
  }, [receiver.socketId, receiver.name, currentSessionId, isScreenSharing, callType, callStatus, logWebRTCError]);

  const handleWebRTCCallEnded = useCallback(() => {
    console.log('📞 WebRTC call ended by remote peer');
    endWebRTCCall();
  }, [endWebRTCCall]);

  const handleWebRTCCallFailed = useCallback((data) => {
    console.log('📞 WebRTC call failed:', data.reason);
    setCallError(`WebRTC call failed: ${data.reason}`);
    setTimeout(() => endWebRTCCall(), 3000);
  }, [endWebRTCCall]);

  const handleWebRTCCallRejected = useCallback((data) => {
    console.log('📞 WebRTC call rejected by receiver');
    setCallError('Call declined');
    setCallStatus('rejected');
    
    // Show rejection notification for 3 seconds then end call
    setTimeout(() => {
      endWebRTCCall();
    }, 3000);
  }, [endWebRTCCall]);

  // Use existing socket connection or fall back to the app-level global socket
  useEffect(() => {
    if (!socket) {
      // Use the shared global socket (already connected and registered in App.js on login).
      // This avoids creating a duplicate registration that would overwrite the user's
      // socket entry in activeUsers and make them appear offline from GlobalCallManager.
      console.log('🔌 No socket prop provided – using global app socket for chat');
      socketRef.current = globalSocket;
      setIsConnected(globalSocket.connected);

      // Keep connection state in sync
      const onConnect = () => { setIsConnected(true); setCallError(''); };
      const onDisconnect = () => { setIsConnected(false); setIsOnline(false); };
      globalSocket.on('connect', onConnect);
      globalSocket.on('disconnect', onDisconnect);

      // Store refs for cleanup
      socketRef._globalOnConnect = onConnect;
      socketRef._globalOnDisconnect = onDisconnect;
    } else {
      console.log('🔌 Using provided socket connection for WebRTC...');
      socketRef.current = socket;
      setIsConnected(socket.connected);
    }

    // Attach WebRTC-specific event listeners
    const attachChatListeners = () => {
      if (!socketRef.current) return;

      // Get receiver user ID
      const receiverUserId = receiver._id || 
                            receiver.userId || 
                            receiver.id || 
                            receiver.doctorId || 
                            receiver.patientId || 
                            receiver.user_id || 
                            receiver.user?._id || 
                            receiver.user?.id || 
                            receiver.doctor?._id || 
                            receiver.patient?._id || 
                            receiver.receiverId ||
                            // Fallback based on current user
                            (currentUser.socketId === '67deb6b8955d923bb6af4812' ? '67deb746955d923bb6af4831' : '67deb6b8955d923bb6af4812');

      console.log('🔍 Setting up chat for receiver:', receiverUserId);

      // Get message history between users
      socketRef.current.emit('get-message-history', {
        userId1: currentUser.socketId,
        userId2: receiverUserId
      });
      
      // Check receiver's online status
      socketRef.current.emit('check-user-status', {
        userId: receiverUserId
      });

      // Direct message receiving
      socketRef.current.on('receive-direct-message', (message) => {
        console.log('📨 Received direct message:', message);
        if (message && !processedMessageIds.current.has(message.id)) {
          processedMessageIds.current.add(message.id);
          setMessages(prev => [...prev, {
            ...message,
            timestamp: new Date(message.timestamp)
          }]);
        }
      });

      // Legacy message receiving events (for backward compatibility)
      socketRef.current.on('receive-message', (message) => {
        console.log('📨 Received message via socket:', message);
        if (message && !processedMessageIds.current.has(message.id)) {
          processedMessageIds.current.add(message.id);
          setMessages(prev => [...prev, {
            ...message,
            timestamp: new Date(message.timestamp)
          }]);
        }
      });

      socketRef.current.on('receive_message', (message) => {
        console.log('📨 Received message via receive_message:', message);
        if (message && !processedMessageIds.current.has(message.id)) {
          processedMessageIds.current.add(message.id);
          setMessages(prev => [...prev, {
            ...message,
            timestamp: new Date(message.timestamp)
          }]);
        }
      });

      socketRef.current.on('message', (message) => {
        console.log('📨 Received message via message event:', message);
        if (message && !processedMessageIds.current.has(message.id)) {
          processedMessageIds.current.add(message.id);
          setMessages(prev => [...prev, {
            ...message,
            timestamp: new Date(message.timestamp)
          }]);
        }
      });

      // Message history event
      socketRef.current.on('message-history', (messages) => {
        console.log('📚 Received message history:', messages.length, 'messages');
        if (messages && Array.isArray(messages)) {
          const validMessages = messages.filter(msg => msg && msg.id);
          validMessages.forEach(msg => processedMessageIds.current.add(msg.id));
          setMessages(validMessages.map(msg => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          })));
        }
      });

      // User status events
      socketRef.current.on('user-status-response', (data) => {
        console.log('👤 User status update:', data);
        if (data.userId === receiverUserId) {
          setIsOnline(data.isOnline);
        }
      });

      socketRef.current.on('user-online', (userId) => {
        console.log('🟢 User came online:', userId);
        if (userId === receiverUserId) {
          setIsOnline(true);
        }
      });

      socketRef.current.on('user-offline', (userId) => {
        console.log('🔴 User went offline:', userId);
        if (userId === receiverUserId) {
          setIsOnline(false);
        }
      });

      // WebRTC-specific socket events – listen on BOTH legacy and new webrtc-* names
      // so this component interoperates with Chat.js and ChatAndCall.js callers/callees
      socketRef.current.on('call-offer', handleWebRTCOffer);
      socketRef.current.on('webrtc-call-offer', handleWebRTCOffer);
      socketRef.current.on('call-answer', handleWebRTCAnswer);
      socketRef.current.on('webrtc-call-answer', handleWebRTCAnswer);
      socketRef.current.on('ice-candidate', handleWebRTCIceCandidate);
      socketRef.current.on('webrtc-ice-candidate', handleWebRTCIceCandidate);
      socketRef.current.on('call-ended', handleWebRTCCallEnded);
      socketRef.current.on('webrtc-call-ended', handleWebRTCCallEnded);
      socketRef.current.on('call-rejected', handleWebRTCCallRejected);
      socketRef.current.on('webrtc-call-rejected', handleWebRTCCallRejected);

      socketRef.current.on('error', (error) => {
        console.error('Socket error:', error);
        logWebRTCError('Socket', new Error(error.message || 'Socket error occurred'));
      });
    };

    // Monitor socket connection status
    const handleConnect = () => {
      console.log('✅ Socket connected for chat');
      setIsConnected(true);
      setCallError('');
    };

    const handleDisconnect = (reason) => {
      console.log('❌ Socket disconnected for chat:', reason);
      setIsConnected(false);
      setIsOnline(false);
      
      // Don't end WebRTC call on socket disconnect - only set status
      if (isInCall) {
        console.log('🔄 Socket disconnected during call - call continues...');
        setCallError('Connection lost - call may continue on WebRTC');
      }
    };

    socketRef.current.on('connect', handleConnect);
    socketRef.current.on('disconnect', handleDisconnect);

    // Attach chat-specific listeners
    attachChatListeners();

    // Cleanup function
    return () => {
      if (socketRef.current) {
        // Remove only chat-specific listeners, don't disconnect the main socket
        socketRef.current.off('receive-direct-message');
        socketRef.current.off('receive-message');
        socketRef.current.off('receive_message');
        socketRef.current.off('message');
        socketRef.current.off('message-history');
        socketRef.current.off('user-status-response');
        socketRef.current.off('user-online');
        socketRef.current.off('user-offline');
        // legacy call events
        socketRef.current.off('call-offer');
        socketRef.current.off('call-answer');
        socketRef.current.off('ice-candidate');
        socketRef.current.off('call-ended');
        socketRef.current.off('call-rejected');
        // new webrtc-* call events
        socketRef.current.off('webrtc-call-offer');
        socketRef.current.off('webrtc-call-answer');
        socketRef.current.off('webrtc-ice-candidate');
        socketRef.current.off('webrtc-call-ended');
        socketRef.current.off('webrtc-call-rejected');
        socketRef.current.off('error');
        socketRef.current.off('connect', handleConnect);
        socketRef.current.off('disconnect', handleDisconnect);
        // Remove global socket connection-state listeners if they were added
        if (socketRef._globalOnConnect) globalSocket.off('connect', socketRef._globalOnConnect);
        if (socketRef._globalOnDisconnect) globalSocket.off('disconnect', socketRef._globalOnDisconnect);
        socketRef.current = null;
      }
    };
  }, [socket, currentUser?.socketId, receiver, handleWebRTCOffer, handleWebRTCAnswer, handleWebRTCIceCandidate, handleWebRTCCallEnded, handleWebRTCCallFailed, handleWebRTCCallRejected, logWebRTCError, isInCall]);

  // Signal globally that this chat window is open so GlobalCallManager defers to us
  useEffect(() => {
    window.__chatWindowOpen = true;
    return () => { window.__chatWindowOpen = false; };
  }, []);

  // Cleanup on unmount - only cleanup media, don't auto-end calls
  useEffect(() => {
    return () => {
      // Only cleanup media streams if component is unmounting
      // Don't auto-end calls due to re-renders or socket issues
      if (localMediaStream.current) {
        localMediaStream.current.getTracks().forEach(track => {
          track.stop();
        });
      }
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Enhanced direct message sending with WebRTC data channel support
  const sendMessage = useCallback(() => {
    if (!newMessage.trim()) {
      return;
    }

    // Get receiver user ID
    const receiverUserId = receiver._id || 
                          receiver.userId || 
                          receiver.id || 
                          receiver.doctorId || 
                          receiver.patientId || 
                          receiver.user_id || 
                          receiver.user?._id || 
                          receiver.user?.id || 
                          receiver.doctor?._id || 
                          receiver.patient?._id || 
                          receiver.receiverId ||
                          // Fallback based on current user
                          (currentUser.socketId === '67deb6b8955d923bb6af4812' ? '67deb746955d923bb6af4831' : '67deb6b8955d923bb6af4812');

    const message = {
      id: Date.now() + Math.random(),
      text: newMessage.trim(),
      sender: currentUser.socketId,
      receiver: receiverUserId,
      timestamp: new Date(),
      type: 'text'
    };

    try {
      // Try to send via WebRTC data channel first (if in call)
      if (dataChannel.current && dataChannel.current.readyState === 'open') {
        dataChannel.current.send(JSON.stringify({
          type: 'chat',
          ...message
        }));
        console.log('📨 Direct message sent via WebRTC data channel');
      } else if (socketRef.current && socketRef.current.connected) {
        // Send direct message via socket
        socketRef.current.emit('send-direct-message', message);
        console.log('📨 Direct message sent via socket to:', receiverUserId);
      }
      
      // Add message to local state immediately for better UX
      if (!processedMessageIds.current.has(message.id)) {
        processedMessageIds.current.add(message.id);
        setMessages(prev => [...prev, message]);
      }
      
      setNewMessage('');
    } catch (error) {
      logWebRTCError('Send Message', error);
    }
  }, [newMessage, currentUser.socketId, receiver, logWebRTCError]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  const handleEmojiClick = useCallback((emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  }, []);

  const toggleEmojiPicker = useCallback(() => {
    setShowEmojiPicker(!showEmojiPicker);
  }, [showEmojiPicker]);

  const formatTime = useCallback((timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }, []);

  // Validation check
  if (!currentUser || !currentUser.socketId || !receiver) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
      }}>
        <div style={{ color: '#666', fontSize: '16px', marginBottom: '16px' }}>
          Unable to load chat - invalid user data
        </div>
        <button onClick={onClose} style={{
          backgroundColor: '#128C7E',
          color: 'white',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '4px',
          cursor: 'pointer'
        }}>
          Close
        </button>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); box-shadow: 0 10px 30px rgba(0,0,0,0.3); }
            50% { transform: scale(1.05); box-shadow: 0 15px 40px rgba(37,211,102,0.4); }
            100% { transform: scale(1); box-shadow: 0 10px 30px rgba(0,0,0,0.3); }
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
      }}>
        
        {/* WebRTC Incoming Call Screen */}
        {incomingCall && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#128C7E',
            background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 9999,
            color: 'white',
            padding: '60px 40px 40px'
          }}>
            <div style={{
              fontSize: '18px',
              fontWeight: '400',
              opacity: 0.9,
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              Incoming WebRTC {incomingCall.callType} call
            </div>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flex: 1,
              justifyContent: 'center'
            }}>
              <div style={{
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                backgroundColor: '#25D366',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '48px',
                fontWeight: 'bold',
                marginBottom: '30px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                border: '4px solid rgba(255,255,255,0.2)',
                animation: 'pulse 2s infinite'
              }}>
                {incomingCall.callerName.charAt(0).toUpperCase()}
              </div>
              
              <div style={{
                fontSize: '32px',
                fontWeight: '300',
                marginBottom: '10px',
                textAlign: 'center'
              }}>
                {incomingCall.callerName}
              </div>

              <div style={{
                fontSize: '16px',
                opacity: 0.8,
                textAlign: 'center'
              }}>
                {incomingCall.callType === 'video' ? '📹 WebRTC Video calling...' : '📞 WebRTC Voice calling...'}
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              width: '100%',
              maxWidth: '300px',
              marginTop: '60px'
            }}>
              <button
                onClick={rejectWebRTCCall}
                style={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  backgroundColor: '#ff4444',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(255,68,68,0.4)'
                }}
              >
                <CallEnd style={{ fontSize: '32px', color: 'white' }} />
              </button>

              <button
                onClick={acceptWebRTCCall}
                style={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  backgroundColor: '#25D366',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(37,211,102,0.4)'
                }}
              >
                {incomingCall.callType === 'video' ? 
                  <VideoCall style={{ fontSize: '32px', color: 'white' }} /> :
                  <Phone style={{ fontSize: '32px', color: 'white' }} />
                }
              </button>
            </div>
          </div>
        )}

        {/* Chat Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          backgroundColor: '#128C7E',
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }}>
          <IconButton 
            onClick={onClose} 
            style={{ color: 'white', marginRight: '8px' }}
          >
            <ArrowBack />
          </IconButton>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: '#25D366',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px',
            fontSize: '16px',
            fontWeight: 'bold'
          }}>
            {receiver.name.charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '500', fontSize: '16px' }}>
              {receiver.name}
            </div>
            <div style={{ fontSize: '13px', opacity: 0.8 }}>
              {isConnected ? (isOnline ? 'Online' : 'Offline') : 'Connecting...'}
              {isInCall && <span> • WebRTC Call Active</span>}
            </div>
          </div>
          {!isInCall ? (
            <>
              <IconButton 
                style={{ color: 'white', marginLeft: '8px' }}
                onClick={() => startWebRTCCall(false)}
                title="Start WebRTC Audio Call"
              >
                <Phone />
              </IconButton>
              <IconButton 
                style={{ color: 'white', marginLeft: '4px' }}
                onClick={() => startWebRTCCall(true)}
                title="Start WebRTC Video Call"
              >
                <VideoCall />
              </IconButton>
            </>
          ) : (
            <IconButton 
              style={{ color: '#ff4444', marginLeft: '8px' }}
              onClick={endWebRTCCall}
              title="End WebRTC Call"
            >
              <CallEnd />
            </IconButton>
          )}
          <IconButton style={{ color: 'white', marginLeft: '4px' }}>
            <MoreVert />
          </IconButton>
        </div>

        {/* WebRTC Video Call Area */}
        {console.log('🔍 Render: isInCall =', isInCall, 'callStatus =', callStatus, 'callType =', callType) || (isInCall || callStatus === 'calling' || callStatus === 'ringing') && (
          <div style={{
            position: 'fixed',
            top: '5vh',
            left: '5vw',
            width: '90vw',
            height: '90vh',
            backgroundColor: '#000',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            {callType === 'video' && (
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  width: '120px',
                  height: '90px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '2px solid white'
                }}
              />
            )}
            
            {/* WebRTC Call Status Overlay */}
            {callStatus && callStatus !== 'connected' && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: callStatus === 'rejected' ? 'rgba(255,68,68,0.9)' : 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '16px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                textAlign: 'center'
              }}>
                {callStatus === 'calling' && '📞 Starting WebRTC call...'}
                {callStatus === 'ringing' && '📞 WebRTC call ringing...'}
                {callStatus === 'connecting' && '📞 WebRTC connecting...'}
                {callStatus === 'rejected' && (
                  <div>
                    <div style={{ fontSize: '18px', marginBottom: '8px' }}>📞❌</div>
                    <div>Call Declined</div>
                    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '4px' }}>
                      The user declined your call
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* WebRTC Status Indicator */}
            <div style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              backgroundColor: 'rgba(0,0,0,0.6)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              🔗 WebRTC {callType === 'video' ? 'Video' : 'Audio'} Call
            </div>
            
            {/* WebRTC Call Error Overlay */}
            {callError && (
              <div style={{
                position: 'absolute',
                top: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(255,0,0,0.9)',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                maxWidth: '80%',
                textAlign: 'center'
              }}>
                {callError}
              </div>
            )}
            
            {/* WebRTC Call Controls */}
            <div style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '16px',
              backgroundColor: 'rgba(0,0,0,0.6)',
              padding: '12px 20px',
              borderRadius: '30px'
            }}>
              <IconButton
                onClick={toggleWebRTCMute}
                style={{
                  backgroundColor: isMuted ? '#ff4444' : 'rgba(255,255,255,0.2)',
                  color: 'white',
                  width: '50px',
                  height: '50px'
                }}
                title={isMuted ? 'Unmute WebRTC Audio' : 'Mute WebRTC Audio'}
              >
                {isMuted ? <MicOff /> : <Mic />}
              </IconButton>

              {callType === 'video' && (
                <IconButton
                  onClick={toggleWebRTCVideo}
                  style={{
                    backgroundColor: isVideoOff ? '#ff4444' : 'rgba(255,255,255,0.2)',
                    color: 'white',
                    width: '50px',
                    height: '50px'
                  }}
                  title={isVideoOff ? 'Turn On WebRTC Video' : 'Turn Off WebRTC Video'}
                >
                  {isVideoOff ? <VideocamOff /> : <Videocam />}
                </IconButton>
              )}

              <IconButton
                onClick={toggleWebRTCSpeaker}
                style={{
                  backgroundColor: isSpeakerOn ? '#25D366' : 'rgba(255,255,255,0.2)',
                  color: 'white',
                  width: '50px',
                  height: '50px'
                }}
                title={isSpeakerOn ? 'Turn Off Speaker' : 'Turn On Speaker'}
              >
                {isSpeakerOn ? <VolumeUp /> : <VolumeOff />}
              </IconButton>

              {callType === 'video' && (
                <IconButton
                  onClick={toggleScreenShare}
                  style={{
                    backgroundColor: isScreenSharing ? '#25D366' : 'rgba(255,255,255,0.2)',
                    color: 'white',
                    width: '50px',
                    height: '50px'
                  }}
                  title={isScreenSharing ? 'Stop Screen Share' : 'Start Screen Share'}
                >
                  {isScreenSharing ? <StopScreenShare /> : <ScreenShare />}
                </IconButton>
              )}

              <IconButton
                onClick={endWebRTCCall}
                style={{
                  backgroundColor: '#ff4444',
                  color: 'white',
                  width: '50px',
                  height: '50px'
                }}
                title="End WebRTC Call"
              >
                <CallEnd />
              </IconButton>
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div style={{
          height: isInCall ? '200px' : '400px',
          overflowY: 'auto',
          padding: '16px',
          backgroundColor: '#E5DDD5',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M20 20c0 11.046-8.954 20-20 20v20h40V20H20z'/%3E%3C/g%3E%3C/svg%3E")`
        }}>
          {messages.length === 0 ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#666',
              fontSize: '14px'
            }}>
              Start a WebRTC-powered conversation...
            </div>
          ) : (
            messages.map((message) => {
              const isOwn = message.sender === currentUser.socketId;
              return (
                <div key={message.id} style={{
                  display: 'flex',
                  justifyContent: isOwn ? 'flex-end' : 'flex-start',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    maxWidth: '70%',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    backgroundColor: isOwn ? '#DCF8C6' : 'white',
                    boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
                    position: 'relative'
                  }}>
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      lineHeight: '1.4'
                    }}>{message.text}</p>
                    <div style={{
                      fontSize: '11px',
                      color: '#666',
                      marginTop: '4px',
                      textAlign: 'right'
                    }}>
                      {formatTime(message.timestamp)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area with WebRTC Data Channel Status */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 16px',
          backgroundColor: 'white',
          borderRadius: '0 0 8px 8px',
          borderTop: '1px solid #e0e0e0'
        }}>
          <IconButton style={{ color: '#666' }}>
            <AttachFile />
          </IconButton>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={dataChannel.current && dataChannel.current.readyState === 'open' ? 
              "Type a message (WebRTC Data Channel)..." : 
              "Type a message..."
            }
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              padding: '8px 12px',
              borderRadius: '20px',
              backgroundColor: '#f5f5f5',
              margin: '0 8px',
              fontSize: '14px'
            }}
          />
          <div style={{ position: 'relative' }}>
            <IconButton 
              style={{ color: '#666', marginRight: '8px' }}
              onClick={toggleEmojiPicker}
            >
              <EmojiEmotions />
            </IconButton>
            {showEmojiPicker && (
              <div style={{
                position: 'absolute',
                bottom: '45px',
                right: '0',
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '12px',
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000
              }}>
                {emojis.map((emoji, index) => (
                  <button
                    key={index}
                    onClick={() => handleEmojiClick(emoji)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '20px',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '4px'
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
          <IconButton 
            onClick={sendMessage}
            style={{
              backgroundColor: dataChannel.current && dataChannel.current.readyState === 'open' ? '#25D366' : '#128C7E',
              color: 'white',
              padding: '8px',
              borderRadius: '50%',
              minWidth: '40px',
              height: '40px'
            }}
            title={dataChannel.current && dataChannel.current.readyState === 'open' ? 
              "Send via WebRTC Data Channel" : 
              "Send via Socket"
            }
          >
            <Send />
          </IconButton>
        </div>
      </div>
    </>
  );
};

export default ChatAndCall;
